import React from 'react';
import { ContentConfig } from 'component/page/detail/DetailPageModularize/contentRegistry.type';
import { ContentRegistry } from 'component/page/detail/DetailPageModularize/ContentRegistry';
import { getCustomMetaFromContentHandler } from 'component/page/detail/DetailPageModularize/ContentRegistryHelpers';
import { ContentWrapper } from 'component/page/detail/DetailPageModularize/ContentWrapper/ContentWrapper';
import { connect, useSelector } from 'react-redux';
import { selectPrerenderStateFromBootstrap } from 'store/slice/bootstrap';
import DetailInViewWrapper from 'component/common/DetailInViewWrapper/DetailInViewWrapper';
import { setSelectedTabOnDetailsPage } from 'component/page/detail/store/details';
import { createSelector } from '@reduxjs/toolkit';
import { selectDetailState } from 'component/page/detail/store/detail.selectors';
import { RootState } from 'store/store.types';

interface DynamicContentProps<T extends ContentConfig['content']> {
    config: Extract<ContentConfig, { content: T }>;
}

interface StateProps {
    isScrolling: boolean;
}

const mapStateToProps = createSelector(selectDetailState, (state) => ({
    isScrolling: state.isScrolling ?? false,
}));

const DynamicContent = React.memo<DynamicContentProps<ContentConfig['content']> & StateProps>(({ config, isScrolling }) => {
    const isPrerender = useSelector(selectPrerenderStateFromBootstrap);
    const reduxState = useSelector((state: RootState) => state);

    const { wrapperProps, contentProps, content } = config;
    const isPrerenderHide = ContentRegistry[content].isPrerenderHide;
    const contentValidation = ContentRegistry[content].contentValidation;

    if (isPrerenderHide && isPrerender) {
        return null;
    }

    if (contentValidation && !contentValidation(reduxState)) {
        return null;
    }

    const { createComponent } = ContentRegistry[content];

    const Component = createComponent() as React.ComponentType<typeof contentProps>;

    if (!Component) {
        return null;
    }

    const customMeta = getCustomMetaFromContentHandler(config, reduxState);

    return (
        <DetailInViewWrapper tabHref={customMeta.href} isScrolling={isScrolling} setSelectedTabCallback={setSelectedTabOnDetailsPage}>
            <div id={customMeta.href}>
                <ContentWrapper {...wrapperProps} content={content} customMeta={customMeta}>
                    <Component {...contentProps} />
                </ContentWrapper>
            </div>
        </DetailInViewWrapper>
    );
});

DynamicContent.displayName = 'DynamicContent';

export default connect(mapStateToProps)(DynamicContent);
